import { Input } from '@frontend/shadcn/components/ui/input';
import {
  PlusIcon,
  MinusIcon,
  FileLinesIcon,
  GripDotsVerticalIcon,
  XmarkIcon,
  CheckIcon,
  PenIcon,
  TrashIcon,
} from '@revfluence/fresh-icons/solid/esm';
import React, { useState, useMemo } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { RichTextEditor } from '@frontend/app/refresh-components';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Modal, Switch } from '@revfluence/fresh';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { SelectCombobox } from '@frontend/app/refresh-components/SelectCombobox';
import { cloneDeep } from 'lodash';
import { MemberFieldSchemasQuery_schemas } from '@frontend/app/queries/types/MemberFieldSchemasQuery';
import { useRecruitment, useFormPage } from '../RecruitmentContext';
import { NewFormFieldModal } from './NewFormFieldModal';
import { IField } from '../../../LandingPages/types';
import { useSchemas } from '../../../LandingPages/LeftPanel/hooks';
import { ADDRESS_FIELD_NAMES, convertFieldsToSelectItems } from '../utils/convertFieldsToSelectItems';
import { ADDRESS_GROUP_FIELD_ID } from '../constants';

/**
 * Helper function to expand Address field into multiple address subfields
 * @param newField - The field configuration containing Address settings
 * @param currentFields - The current fields array
 * @param allSchemas - All available schemas
 * @param alreadyAddedFields - Fields already added in other forms to avoid duplicates
 * @returns Updated fields array with address subfields added
 */
const expandAddressFields = (newField: IField, currentFields: IField[], allSchemas: MemberFieldSchemasQuery_schemas[], alreadyAddedFields: IField[] = []): IField[] => {
  const addressFieldsToAdd = allSchemas.filter(
    (schema) => ADDRESS_FIELD_NAMES.includes(schema.name) &&
    !currentFields.some((field) => field.id === schema.id) &&
    !alreadyAddedFields.some((field) => field.id === schema.id)
  );

  return addressFieldsToAdd.map((schema) => ({
    id: schema.id,
    name: schema.name,
    dbName: schema.name,
    type: schema.type,
    label: schema.name,
    displayLabel: schema.name,
    required: newField.required,
    choices: schema.choices,
    order: newField.order,
    source: newField.source,
    active: true,
  }));
};

export const FormEditor = () => {
  const {
    amplifyTemplate,
    setFormEditorTitle,
    setFormEditorDescription,
    setFormEditorSupportEmail,
    setFormEditorPrimaryButtonLabel,
    setFormFields,
    addFormEditorField,
    removeFormEditorField,
    editFormEditorField,
    reorderFormEditorFields,
    setShowNewFormFieldModal,
  } = useRecruitment();

  const { nonAppSchemas, appSchemas, refetchSchemas } = useSchemas();

  // we allow user to modify non-app-schemas and app-schemas only if schema.metaData.edtiable
  const allSchemas = useMemo(
    () => [...nonAppSchemas, ...appSchemas.filter((schema) => schema.metaData?.editable)],
    [nonAppSchemas, appSchemas],
  );

  const [isAddingField, setIsAddingField] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
  const [newField, setNewField] = useState<IField>({
    id: null,
    name: null,
    dbName: null,
    type: null,
    label: null,
    displayLabel: null,
    required: false,
    choices: null,
    order: null,
    source: null,
  });

  const formPage = useFormPage();
  const alreadyAddedFields = useMemo(() => {
    if (amplifyTemplate) {
      return amplifyTemplate.pages.map((page) => page?.home?.form?.fields).filter(Boolean)[0] || [];
    }
    return [];
  }, [amplifyTemplate]);

  if (!formPage) {
    return null;
  }

  const { title, description, fields, primaryButtonLabel, supportEmail } = formPage;

  const handleChangeFieldSchema = (schemaId: string) => {
    if (schemaId === ADDRESS_GROUP_FIELD_ID.toString()) {
      setNewField({
        ...newField,
        id: ADDRESS_GROUP_FIELD_ID,
        name: 'Address',
        source: 'schema',
      });
      return;
    }

    const schema = allSchemas.find((schema) => schema.id === Number(schemaId));
    if (schema) {
      setNewField({
        ...newField,
        id: schema.id,
        name: schema.name,
        type: schema.type,
        label: schema.name,
        displayLabel: editingFieldId ? newField.displayLabel : schema.name,
        choices: schema.choices,
        source: 'schema',
        order: fields.length,
      });
    }
  };

  const handleAddField = () => {
    if (newField.name === 'Address') {
      const addressFields = expandAddressFields(newField, fields, allSchemas, alreadyAddedFields);
      setFormFields([...fields, ...addressFields]);
      setIsAddingField(false);
      setNewField({
        id: null,
        name: null,
        dbName: null,
        type: null,
        label: null,
        displayLabel: null,
        required: false,
        choices: null,
        order: null,
        source: null,
      });
      return;
    }
    addFormEditorField({
      ...newField,
      order: fields.length,
    });
    setIsAddingField(false);
    setNewField({
      id: null,
      name: null,
      dbName: null,
      type: null,
      label: null,
      displayLabel: null,
      required: false,
      choices: null,
      order: null,
      source: null,
    });
  };

  const handleEditField = (fieldId: number) => {
    setEditingFieldId(fieldId);
    const field = fields.find((field) => field.id === fieldId);
    if (field) {
      setNewField({
        id: field.id,
        name: field.name,
        dbName: field.dbName,
        type: field.type,
        label: field.label,
        displayLabel: field.displayLabel,
        required: field.required,
        choices: field.choices,
        order: field.order,
        source: field.source,
        active: true,
      });
    }
  };

  const handleCancelEditField = () => {
    setEditingFieldId(null);
    setNewField({
      id: null,
      name: null,
      dbName: null,
      type: null,
      label: null,
      displayLabel: null,
      required: false,
      choices: null,
      order: null,
      source: null,
    });
  };

  const handleSaveEditedField = (fieldId: number) => {
    if (newField.name === 'Address') {
      const addressFields = expandAddressFields(newField, fields, allSchemas, alreadyAddedFields);
      const fieldToReplaceIndex = fields.findIndex((field) => field.id === fieldId);
      const newFieldsWithEdit = cloneDeep(fields);
      newFieldsWithEdit.splice(fieldToReplaceIndex, 1, ...addressFields);
      setFormFields(newFieldsWithEdit);
      setEditingFieldId(null);
      setNewField({
        id: null,
        name: null,
        dbName: null,
        type: null,
        label: null,
        displayLabel: null,
        required: false,
        choices: null,
        order: null,
        source: null,
      });
      return;
    }
    editFormEditorField(fieldId, newField);
    setEditingFieldId(null);
    setNewField({
      id: null,
      name: null,
      dbName: null,
      type: null,
      label: null,
      displayLabel: null,
      required: false,
      choices: null,
      order: null,
      source: null,
    });
  };

  const handleRemoveField = (fieldId: number) => {
    Modal.confirm({
      title: 'Remove Field',
      content: 'Are you sure you want to remove this field?',
      onOk: () => {
        removeFormEditorField(fieldId);
        setEditingFieldId(null);
      },
    });
  };

  const handleCancelAddField = () => {
    setIsAddingField(false);
    setNewField({
      id: null,
      name: null,
      dbName: null,
      type: null,
      label: null,
      displayLabel: null,
      required: false,
      choices: null,
      order: null,
      source: null,
    });
  };

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    reorderFormEditorFields(items.map((item, index) => ({ ...item, order: index })));
  };

  const handleSaveNewField = async (field: IField) => {
    await refetchSchemas();
    handleChangeFieldSchema(field.id.toString());
  };

  const hasDescription = !!description;
  return (
    <Accordion type="multiple" className="[&_h3]:mb-0" defaultValue={['hero', 'fields']}>
      <AccordionItem value="hero" className="border-b-0">
        <AccordionTrigger>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-5 rounded-lg text-primary-foreground flex items-center justify-center">
              <FileLinesIcon fontSize={12} />
            </div>
            <span className="text-base font-medium">Form</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pb-0">
          {/* Title Section */}
          <div className="flex flex-col gap-2 mt-2">
            <label htmlFor="title" className="text-xs font-medium text-foreground">
              Title <span className="text-primaryMuted font-normal">(Required)</span>
            </label>
            <div className="px-[1px]">
              <Input
                id="title"
                placeholder="Campaign Brief"
                value={title}
                onChange={(e) => setFormEditorTitle(e.target.value)}
              />
            </div>
          </div>

          {/* Description Section */}
          <div className="mt-2">
            {hasDescription && (
              <div className="flex flex-col items-start gap-2">
                <Button variant="link" className="gap-2 pl-0" onClick={() => setFormEditorDescription('')} size="xs">
                  <MinusIcon />
                  Remove Description
                </Button>
                <RichTextEditor
                  placeholder="Please review and agree to the details below to continue with your application."
                  value={description}
                  onChange={setFormEditorDescription}
                  className="[&_.ql-editor]:min-h-[100px]"
                />
              </div>
            )}
            {!hasDescription && (
              <Button
                variant="link"
                className="gap-2 pl-0"
                onClick={() => setFormEditorDescription('Please fill out the form below to apply.')}
                size="xs"
              >
                <PlusIcon />
                Add Description
              </Button>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="fields" className="border-b-0">
        <AccordionTrigger>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-5 rounded-lg text-primary-foreground flex items-center justify-center">
              <FileLinesIcon fontSize={12} />
            </div>
            <span className="text-base font-medium">Form Fields</span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col justify-start items-start gap-2">
              <div className="self-stretch flex justify-between items-center gap-4">
                <div className="flex-1 text-foreground text-xs font-medium">Selected Fields</div>
                <Button
                  variant="link"
                  size="xs"
                  disabled={isAddingField || !!editingFieldId}
                  onClick={() => setIsAddingField(true)}
                >
                  <PlusIcon className="mr-2" />
                  Add Field
                </Button>
              </div>

              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="formFields">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="self-stretch flex flex-col gap-2"
                    >
                      {fields?.map((field, index) => (
                        <Draggable key={field.id.toString()} draggableId={field.id.toString()} index={index}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} className="self-stretch">
                              {editingFieldId === field.id ? (
                                <div className="px-3 py-2 bg-background rounded-xl border border-border flex flex-col gap-2">
                                  <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                      <div {...provided.dragHandleProps} className="flex items-center justify-center">
                                        <GripDotsVerticalIcon fontSize={12} className="text-grey-3" />
                                      </div>
                                      <label className="text-xs font-medium">Edit Field</label>
                                      <div className="flex items-center gap-2 ml-auto">
                                        <Switch
                                          size="small"
                                          checked={newField.required}
                                          onChange={(checked) => setNewField({ ...newField, required: checked })}
                                        />
                                        <span className="text-foreground text-sm">Required</span>
                                      </div>
                                    </div>
                                    <SelectCombobox
                                      items={convertFieldsToSelectItems(allSchemas, [...fields, ...alreadyAddedFields])}
                                      value={newField.id?.toString()}
                                      onValueChange={(value) => handleChangeFieldSchema(value)}
                                      filterPredicate={(item, searchTerm) => {
                                        const schema = allSchemas.find((s) => s.id.toString() === item.value);
                                        return schema?.name.toLowerCase().includes(searchTerm.toLowerCase());
                                      }}
                                      placeholder="Select field(s) to add"
                                    />
                                    {newField.id && newField.name !== 'Address' && (
                                      <div className="flex flex-col gap-1">
                                        <label className="text-xs font-medium text-foreground">Creator Display Label</label>
                                        <Input
                                          placeholder={newField.label || 'Enter creator-facing label'}
                                          value={newField.displayLabel || ''}
                                          onChange={(e) => setNewField({ ...newField, displayLabel: e.target.value })}
                                          className="text-sm bg-primary-foreground"
                                        />
                                        <span className="text-xs text-grey-4">How this field appears to creators (leave blank to use default)</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="link"
                                      size="xs"
                                      className="self-start px-0"
                                      onClick={() => setShowNewFormFieldModal(true)}
                                    >
                                      <PlusIcon className="mr-2" />
                                      Custom Field
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="smallIcon"
                                      className="ml-auto text-destructive"
                                      onClick={() => handleRemoveField(field.id)}
                                    >
                                      <TrashIcon fontSize={12} />
                                    </Button>
                                    <Button variant="outline" size="smallIcon" onClick={handleCancelEditField}>
                                      <XmarkIcon fontSize={12} />
                                    </Button>
                                    <Button
                                      size="smallIcon"
                                      onClick={() => handleSaveEditedField(field.id)}
                                      disabled={!newField.id}
                                    >
                                      <CheckIcon fontSize={12} />
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="px-3 py-2 bg-card rounded-xl border border-border flex items-center gap-2 min-h-10">
                                  <div {...provided.dragHandleProps} className="flex items-center justify-center">
                                    <GripDotsVerticalIcon fontSize={12} className="text-grey-3" />
                                  </div>
                                  <div className="flex-1 text-xs font-medium">
                                    <span className="text-foreground">{field.displayLabel || field.label}</span>
                                    {field.displayLabel && field.displayLabel !== field.label && (
                                      <span className="text-grey-4 ml-1">({field.label})</span>
                                    )}
                                    {field.required && <span className="text-red-4">*</span>}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="smallIcon"
                                    onClick={() => handleEditField(field.id)}
                                    disabled={!!editingFieldId || isAddingField || field.source === 'dbColumn'}
                                  >
                                    <PenIcon fontSize={12} />
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {/* Add New Field Section */}
              {isAddingField && (
                <div className="self-stretch px-3 py-2 bg-background rounded-xl border border-border flex flex-col gap-2">
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-medium">Add Field</label>
                      <div className="flex items-center gap-2">
                        <Switch
                          size="small"
                          checked={newField.required}
                          onChange={(checked) => setNewField({ ...newField, required: checked })}
                        />
                        <span className="text-foreground text-sm">Required</span>
                      </div>
                    </div>
                    <SelectCombobox
                      items={convertFieldsToSelectItems(allSchemas, [...fields, ...alreadyAddedFields])}
                      value={newField.id?.toString()}
                      onValueChange={(value) => handleChangeFieldSchema(value)}
                      filterPredicate={(item, searchTerm) => {
                        const schema = allSchemas.find((schema) => schema.id.toString() === item.value);
                        return schema?.name.toLowerCase().includes(searchTerm.toLowerCase());
                      }}
                      placeholder="Select field(s) to add"
                    />
                    {newField.id && newField.name !== 'Address' && (
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-foreground">Creator Display Label</label>
                        <Input
                          placeholder={newField.label || 'Enter creator-facing label'}
                          value={newField.displayLabel || ''}
                          onChange={(e) => setNewField({ ...newField, displayLabel: e.target.value })}
                          className="text-sm bg-primary-foreground"
                        />
                        <span className="text-xs text-grey-4">How this field appears to creators (leave blank to use default)</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="link"
                      size="xs"
                      className="self-start px-0"
                      onClick={() => setShowNewFormFieldModal(true)}
                    >
                      <PlusIcon className="mr-2" />
                      Custom Field
                    </Button>
                    <Button variant="outline" size="smallIcon" className="ml-auto" onClick={handleCancelAddField}>
                      <XmarkIcon fontSize={12} />
                    </Button>
                    <Button size="smallIcon" onClick={handleAddField} disabled={!newField.id}>
                      <CheckIcon fontSize={12} />
                    </Button>
                  </div>
                </div>
              )}
              <Button variant="outline" className="w-full" size="sm" disabled={isAddingField || !!editingFieldId} onClick={() => setIsAddingField(true)}>
                <PlusIcon className="mr-2" />
                Add Field
              </Button>
            </div>
          </div>
          {/* Support Email Address Section */}
          <div className="mt-4">
            <label htmlFor="supportEmail" className="text-xs font-medium text-foreground">
              Support Email Address
            </label>
            <div className="px-[1px] pb-[1px]">
              <Input
                id="supportEmail"
                placeholder="support@example.com"
                className="mt-2"
                value={supportEmail}
                onChange={(e) => setFormEditorSupportEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="buttonLabel" className="text-xs font-medium text-foreground">
              Primary Button Label <span className="text-primaryMuted">(Required)</span>
            </label>
            <div className="px-[1px] pb-[1px]">
              <Input
                id="buttonLabel"
                placeholder="Continue"
                className="mt-2"
                value={primaryButtonLabel}
                onChange={(e) => setFormEditorPrimaryButtonLabel(e.target.value)}
              />
            </div>
          </div>
          <NewFormFieldModal onSaveField={handleSaveNewField} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
